/* ============================================================================
   PARHELIK — cart controller
   Vanilla, dependency-free, idempotent. Drives the slide-in cart drawer:
     • open  on [data-ph-cart-open] click (and ?cart deep-link / cart link)
     • close on overlay / [data-ph-cart-close] / Esc
     • intercept [data-ph-add-to-cart] forms -> POST /cart/add.js -> open
     • qty change -> /cart/change.js (by line); remove -> /cart/change.js qty 0
     • after each mutation: re-fetch ?sections=cart-drawer, swap #cart-drawer
       inner HTML, refresh every [data-ph-cart-count] from /cart.js item_count
   Public API: window.PHCart.refresh(), .open(), .close(), .addId(id, qty)
   ========================================================================== */
(function () {
  'use strict';

  if (window.PHCart && window.PHCart._installed) return;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var SEL = {
    drawer: '#cart-drawer',
    panel: '.ph-cart__panel',
    open: '[data-ph-cart-open]',
    close: '[data-ph-cart-close]',
    addForm: '[data-ph-add-to-cart]',
    qtyInput: '[data-ph-qty]',
    qtyMinus: '[data-ph-qty-minus]',
    qtyPlus: '[data-ph-qty-plus]',
    remove: '[data-ph-cart-remove]',
    count: '[data-ph-cart-count]'
  };

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function drawer() { return $(SEL.drawer); }

  /* ---- fetch helpers ---- */
  function postJSON(url, data) {
    return fetch(ROOT.replace(/\/$/, '') + url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(data)
    }).then(function (r) { return r.json(); });
  }

  /* ---- count badges ---- */
  function setBusy(state) {
    var d = drawer();
    if (d) d.classList.toggle('is-busy', !!state);
  }

  function updateCounts() {
    return fetch(ROOT.replace(/\/$/, '') + '/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var n = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;
        document.querySelectorAll(SEL.count).forEach(function (el) {
          el.textContent = n;
          el.setAttribute('data-count', n);
          el.classList.toggle('is-empty', n === 0);
        });
        return cart;
      })
      .catch(function () { /* non-fatal */ });
  }

  /* ---- re-render the drawer markup via Section Rendering API ---- */
  function refresh() {
    // NOTE: the Section Rendering API (?sections=cart-drawer) returns EMPTY on
    // JSON templates (product/collection), so requesting it from the current
    // pathname silently fails — the drawer never refreshes after add-to-cart.
    // Always request it from the shop ROOT (where the layout section renders),
    // and fall back to a full root-page fetch if the API still returns empty.
    var base = ROOT.replace(/\/$/, '');
    return fetch(base + '/?sections=cart-drawer', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (json) {
        var html = json && json['cart-drawer'];
        if (html) return html;
        // fallback: re-render from the full root page
        return fetch(base + '/', { cache: 'no-store' }).then(function (r) { return r.text(); });
      })
      .then(function (html) {
        if (!html) { console.warn('[PHCart] cart-drawer markup unavailable — not swapped'); return; }
        var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector(SEL.drawer);
        var current = drawer();
        if (fresh && current) {
          var wasOpen = current.classList.contains('is-open');
          current.innerHTML = fresh.innerHTML;
          if (wasOpen) current.classList.add('is-open');
        }
      })
      .then(updateCounts)
      .catch(function (e) { console.error('[PHCart] refresh failed', e); });
  }

  /* ---- open / close ---- */
  function open() {
    var d = drawer();
    if (!d) return;
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('ph-scroll-lock');
  }

  function close() {
    var d = drawer();
    if (!d) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('ph-scroll-lock');
  }

  /* ---- mutations ---- */
  function changeLine(line, quantity) {
    setBusy(true);
    return postJSON('/cart/change.js', { line: Number(line), quantity: Number(quantity) })
      .then(function () { return refresh(); })
      .finally(function () { setBusy(false); });
  }

  function addId(id, quantity) {
    return postJSON('/cart/add.js', { id: id, quantity: quantity || 1 })
      .then(function (res) {
        if (res && res.status && res.status !== 200) { return res; }
        return refresh().then(function () { open(); return res; });
      });
  }

  function addFromForm(form) {
    var data = new FormData(form);
    var btn = form.querySelector('[type="submit"]');
    if (btn) btn.classList.add('is-loading');
    return fetch(ROOT.replace(/\/$/, '') + '/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: data
    })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (res && res.status && res.status !== 200) {
          console.warn('[PHCart] add error', res.description || res.message);
          return res;
        }
        return refresh().then(function () { open(); return res; });
      })
      .catch(function (e) { console.error('[PHCart] add failed', e); })
      .finally(function () { if (btn) btn.classList.remove('is-loading'); });
  }

  function clampQty(input) {
    var v = parseInt(input.value, 10);
    if (isNaN(v) || v < 1) v = 1;
    input.value = v;
    return v;
  }

  /* ---- delegated events (survive innerHTML swaps) ---- */
  function onClick(e) {
    var t = e.target;

    var openTrigger = t.closest && t.closest(SEL.open);
    if (openTrigger) {
      // only hijack when the cart drawer exists on the page
      if (drawer()) { e.preventDefault(); open(); }
      return;
    }

    if (t.closest && t.closest(SEL.close)) { e.preventDefault(); close(); return; }

    var minus = t.closest && t.closest(SEL.qtyMinus);
    if (minus) {
      e.preventDefault();
      var inDec = minus.parentNode.querySelector(SEL.qtyInput);
      if (inDec) {
        var nDec = Math.max(1, (parseInt(inDec.value, 10) || 1) - 1);
        inDec.value = nDec;
        changeLine(inDec.getAttribute('data-line'), nDec);
      }
      return;
    }

    var plus = t.closest && t.closest(SEL.qtyPlus);
    if (plus) {
      e.preventDefault();
      var inInc = plus.parentNode.querySelector(SEL.qtyInput);
      if (inInc) {
        var nInc = (parseInt(inInc.value, 10) || 0) + 1;
        inInc.value = nInc;
        changeLine(inInc.getAttribute('data-line'), nInc);
      }
      return;
    }

    var rm = t.closest && t.closest(SEL.remove);
    if (rm) {
      e.preventDefault();
      changeLine(rm.getAttribute('data-line'), 0);
      return;
    }
  }

  function onChange(e) {
    var input = e.target.closest && e.target.closest(SEL.qtyInput);
    if (!input) return;
    var line = input.getAttribute('data-line');
    if (line == null) return;
    changeLine(line, clampQty(input));
  }

  function onSubmit(e) {
    var form = e.target.closest && e.target.closest(SEL.addForm);
    if (!form) return;
    e.preventDefault();
    addFromForm(form);
  }

  function onKeydown(e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      var d = drawer();
      if (d && d.classList.contains('is-open')) close();
    }
    // Enter inside a qty input commits the change
    if (e.key === 'Enter') {
      var input = e.target.closest && e.target.closest(SEL.qtyInput);
      if (input) { e.preventDefault(); changeLine(input.getAttribute('data-line'), clampQty(input)); }
    }
  }

  function bind() {
    document.addEventListener('click', onClick);
    document.addEventListener('change', onChange);
    document.addEventListener('submit', onSubmit, true);
    document.addEventListener('keydown', onKeydown);
  }

  /* ---- public API ---- */
  window.PHCart = {
    _installed: true,
    refresh: refresh,
    open: open,
    close: close,
    addId: addId,
    updateCounts: updateCounts
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bind(); updateCounts(); });
  } else {
    bind();
    updateCounts();
  }

  // Re-scan in the theme editor when sections load/reload.
  document.addEventListener('shopify:section:load', function () { updateCounts(); });
})();
